import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { translatePostgrestError } from '../lib/translatePostgrestError';
import { useApp } from './useApp';

// All param levels in resolution order (most specific first)
export const PARAM_LEVELS = [
  'product',
  'brand',
  'category',
  'manufacturer',
  'distributor',
  'business',
] as const;

export type ParamLevel = (typeof PARAM_LEVELS)[number];

/** Row from the `params` table as selected by this hook. */
export interface ParamRow {
  id: string;
  key: string;
  value: number | string | null;
  previous_value?: number | string | null;
  level: string;
  entity_id: string | null;
  /** params' stored column (public.marketplace enum — slice-B family;
   *  NULL = base row, the path-agnostic default). */
  marketplace: string | null;
  source?: string | null;
  set_by?: string | null;
  set_at?: string | null;
  changed_reason?: string | null;
  is_session_override?: boolean;
  label?: string | null;
}

interface ResolveContext {
  distributorId?: string | null;
}

export interface UseParamStoreResult {
  allParams: ParamRow[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  reload: () => Promise<void>;
  getByLevel: (level: string, entityId?: string | null) => ParamRow[];
  allKeys: () => string[];
  resolveKey: (key: string, context?: ResolveContext) => { param: ParamRow; level: string } | null;
  updateParam: (
    id: string,
    newValue: number | string | null,
    changedReason?: string
  ) => Promise<void>;
  insertParam: (fields: Record<string, unknown>) => Promise<void>;
  deleteParam: (id: string) => Promise<void>;
}

export function useParamStore(): UseParamStoreResult {
  const { user } = useApp();
  const [allParams, setAllParams] = useState<ParamRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!user) return;
    setLoading(true);
    const { data, error: err } = await supabase
      .from('params')
      .select('*')
      .eq('is_session_override', false)
      .order('level')
      .order('key');
    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }
    setAllParams((data as unknown as ParamRow[]) ?? []);
    setError(null);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  // Get all params at a given level, optionally filtered by entity
  function getByLevel(level: string, entityId: string | null = null): ParamRow[] {
    return allParams.filter(
      (p) =>
        p.level === level && (entityId !== null ? p.entity_id === entityId : p.entity_id === null)
    );
  }

  // All unique param keys across all levels
  function allKeys(): string[] {
    return [...new Set(allParams.map((p) => p.key))].sort();
  }

  // Resolve a key through the hierarchy, returns first match with its level
  function resolveKey(
    key: string,
    context: ResolveContext = {}
  ): { param: ParamRow; level: string } | null {
    const { distributorId } = context;
    const chain: Array<{ level: string; entityId: string | null }> = [
      { level: 'distributor', entityId: distributorId ?? null },
      { level: 'business', entityId: null },
    ];
    for (const { level, entityId } of chain) {
      if (!entityId && level !== 'business') continue;
      const found = allParams.find(
        (p) =>
          p.key === key &&
          p.level === level &&
          (entityId ? p.entity_id === entityId : p.entity_id === null) &&
          // base-row predicate on params' stored enum column (slice-B family).
          !p.marketplace
      );
      if (found) return { param: found, level };
    }
    return null;
  }

  async function updateParam(
    id: string,
    newValue: number | string | null,
    changedReason = ''
  ): Promise<void> {
    if (!user) throw new Error('Not authenticated');
    const existing = allParams.find((p) => p.id === id);
    setSaving(true);
    setError(null);
    const { error: err } = await supabase
      .from('params')
      .update({
        value: newValue,
        previous_value: existing?.value ?? null,
        changed_reason: changedReason || null,
        set_by: user.id,
        set_at: new Date().toISOString(),
        source: 'manual',
      } as never)
      .eq('id', id);
    setSaving(false);
    if (err) {
      // Day 84.6.5 #P5-δ — translator wiring per §21 PostgREST direct-write
      // pattern (matches insertParam at L164; was raw err.message).
      const ue = translatePostgrestError(err);
      setError(ue.message);
      throw new Error(ue.message);
    }
    await load();
  }

  async function insertParam(fields: Record<string, unknown>): Promise<void> {
    if (!user) throw new Error('Not authenticated');
    setSaving(true);
    setError(null);
    const { error: err } = await supabase.from('params').insert({
      ...fields,
      source: 'manual',
      set_by: user.id,
      set_at: new Date().toISOString(),
    } as never);
    setSaving(false);
    if (err) {
      const ue = translatePostgrestError(err);
      setError(ue.message);
      throw new Error(ue.message);
    }
    await load();
  }

  async function deleteParam(id: string): Promise<void> {
    setSaving(true);
    setError(null);
    const { error: err } = await supabase.from('params').delete().eq('id', id);
    setSaving(false);
    if (err) {
      const ue = translatePostgrestError(err);
      setError(ue.message);
      throw new Error(ue.message);
    }
    await load();
  }

  return {
    allParams,
    loading,
    saving,
    error,
    reload: load,
    getByLevel,
    allKeys,
    resolveKey,
    updateParam,
    insertParam,
    deleteParam,
  };
}
